import React, { useState, useEffect } from 'react'
import {
  IconButton,
  Grid,
  CircularProgress,
  Dialog,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@material-ui/core'
import LambdaFetch from '../functions/FetchFromLambda'
import {usePrintManagerReactTableCols} from '../hooks/PrintManagerCols'
import { smallScreenWidth } from '../constants/AppConstants'
import DeleteIcon from '@mui/icons-material/Delete'
import ReactTable from '../components/ReactTable';

export default function AthenaPrintManager (props) {
  const [state, setstate] = useState({
    printManagerData: [],
    downloadId: null,
    isFetching: false,
    isLoading: true,
    deletingId: null
  })
  const [currentPagination, setCurrentPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  })
  const [params, setParams] = useState(null)
  const [paramsHeader, setParamsHeader] = useState(null)

  const { credentials } = props.fetchInitialData

  // Get timezone-aware columns based on system timezone setting
  const systemTimezone = credentials?.systemTimezone || 'America/New_York'
  
  // Use the hook for React Table columns (with timezone support)
  const printManagerCols = usePrintManagerReactTableCols(systemTimezone)
  
  // Old MUI table columns (kept for reference, not used anymore)
  // const printManagerColsMui = React.useMemo(() => {
  //   return getPrintManagerDynamoCols(systemTimezone)
  // }, [systemTimezone])
  const fetch = async () => {
    try {
      setstate({ ...state, isFetching: true, isLoading: true })

      const resp = await LambdaFetch(
        'dynamo/print-manager',
        'get',
        credentials.user.accessToken,
        null,
        '',
        credentials
      )
            
      if (resp.success && resp.data) {
        const printData = resp.data
          .map(document => {
            return {
              ...document,
              // Map DynamoDB fields to expected format
              id: document.executionId,
              event_name: document.eventName || document.reportName,
              document_link: document.executionId,
              status: document.status,
              size: document.size,
              created_at: document.createdAt,
              report_name: document.reportName,
              username: document.username,
              
              // Add action links
              params_link: document.reportingParams ? (
                <div
                  className='editLink'
                  onClick={() => {
                    setParams(document.reportingParams)
                    setParamsHeader(document.eventName)
                  }}
                >
                  view
                </div>
              ) : (
                ''
              ),
              reportingParams: document.reportingParams || null,
              fail_reason: document.status === 'failed' ? (
                <div className='editLink'
                  onClick={() => {
                    window.alert('Report failed to complete')
                  }}>view</div>
              ) : ''
            }
          })
          .sort((a, b) => {
            // Sort by createdAt in descending order (most recent first)
            const dateA = new Date(a.created_at || 0)
            const dateB = new Date(b.created_at || 0)
            return dateB - dateA
          })
        setstate({ ...state, printManagerData: printData, isFetching: false, isLoading: false })
      } else {
        console.error('Failed to fetch print manager data:', resp.error)
        setstate({ ...state, printManagerData: [], isFetching: false, isLoading: false })
      }
    } catch (e) {
      console.error('Error fetching print manager data:', e)
      setstate({ ...state, printManagerData: [], isFetching: false, isLoading: false })
    }
  }
  useEffect(() => {
    fetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchAthenaFile = async (key, fileName) => {
    setstate({ ...state, downloadId: key })
    try {
      const resp = await LambdaFetch(
        'reports-athena',
        'post',
        credentials.user.accessToken,
        JSON.stringify({
          action: 'get-athena-file',
          doc_link: key,
          file_name: fileName
        }),
        '',
        credentials
      )
      if (resp.success) {
        const downloadUrl = resp.data.presignedUrl
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = ''; // lets Content-Disposition set the filename
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setstate({ ...state, downloadId: null })
    } catch (e) {
      console.log(e)
      setstate({ ...state, downloadId: null })
    }
  }



  const deleteSingleRow = async (executionId) => {
    // Set deleting state
    setstate({ ...state, deletingId: executionId })

    try {
      // Delete the record from DynamoDB
      await LambdaFetch(
        `dynamo/print-manager/item/${executionId}`,
        'delete',
        credentials.user.accessToken,
        null, // No body for DELETE request
        '', // successMessage
        credentials
      )

      // Show success message
      if (props.fetchInitialData?.createSnack) {
        props.fetchInitialData.createSnack('File has been deleted successfully', 'success', 3000)
      }

      // Clear deleting state
      setstate({ ...state, deletingId: null })

      // Refresh the data - pagination will be preserved via currentPagination state
      fetch()
    } catch (e) {
      console.error('Error deleting record:', e)

      // Clear deleting state
      setstate({ ...state, deletingId: null })

      // Show error message
      if (props.fetchInitialData?.createSnack) {
        props.fetchInitialData.createSnack('Error deleting file. Please try again.', 'error', 3000)
      } else {
        window.alert('Error deleting file')
      }
    }
  }

  const handleDeleteRow = (rowIndex, executionId) => {
    deleteSingleRow(executionId)
  }
  // const deletFiles = async (currentFiles) => {
  //   setDeleteData({...deleteData, isLoading: true})
  //   try {
  //     await LambdaFetch(
  //       'get-s3-object',
  //       'post',
  //       credentials.user.accessToken,
  //       JSON.stringify({
  //         action: 'delete',
  //         files: currentFiles
  //       }),
  //       'Successfully deleted file(s)',
  //       credentials
  //     )
  //     setDeleteData({...deleteData, isLoading: false, isOpen: false, files: []})
  //     fetch()
  //   } catch (e) {
  //   }
  // }
  const handleRefresh = () => {
    setstate({ ...state, isFetching: true, isLoading: true })
    fetch()
  }

  const printTableData = state.printManagerData.map((row, index) => {
    row.link =
      row.status.toLowerCase() === 'pending' ? (
        <div style={{ margin: 'auto' }}>pending...</div>
      ) : row.status === 'FAILED' || row.status?.toLowerCase() === 'failed' ? (
        <div style={{ margin: 'auto', color: 'red' }}>FAILED</div>
      ) : (
        <div>
          {state.downloadId === row.id ? (
            <div style={{ paddingLeft: '1rem' }}>
              <CircularProgress
                className='loading-circle'
                disableShrink
                size={16}
              />
            </div>
          ) : state.isFetching ? (
            ''
          ) : (
            <>
              {row.status.toLowerCase() === 'success' && (
                <div
                  className='editLink'
                  onClick={() =>  fetchAthenaFile(row.document_link, row.event_name)}
                >
                  download
                </div>
              )}
            </>
          )}
        </div>
      )

    // Add delete button with loading indicator
    const isDeleting = state.deletingId === row.executionId
    row.delete_action = (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

        <IconButton
          onClick={() => handleDeleteRow(index, row.executionId)}
          size="small"
          disabled={isDeleting}
        >
          {isDeleting ? (
            <CircularProgress
              size={16}
              disableShrink
            />
          ) : (
            <DeleteIcon fontSize="small" />
          )}
        </IconButton>
      </div>
    )

    // Mark row as being deleted for styling
    row._isDeleting = isDeleting

    return row
  })

  return (
    <>
      {params && (
        <Dialog
          fullWidth
          scroll={'paper'}
          open={params}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
        >
          <DialogTitle>{`${paramsHeader} - params`}</DialogTitle>

          <DialogContent dividers>
         <RenderParams data={JSON.parse(params)}/>
            {/* {Object.keys(JSON.parse(params)).map(function (key) {
              return (
                <Typography variant='body1'>{`${key}: ${
                  JSON.parse(params)[key]
                }`}</Typography>
              )
            })} */}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setParams(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      <div
        style={{
          margin:
            props.fetchInitialData.credentials.appWidth < smallScreenWidth
              ? '1rem auto'
              : '1rem'
        }}
      >
        <Grid container>
          <Grid item xs={12} style={{ position: 'relative' }}>

            <ReactTable
            cols={printManagerCols}
            data={printTableData}
            isLoading={state.isLoading}
            initialPagination={currentPagination}
            onPaginationChange={(newPagination) => {
              setCurrentPagination(newPagination)
            }}
            onRefresh={handleRefresh}
            tableKey="print-manager-table"
            initialState={{ columnVisibility: { username: false } }}
            />
            {/* <TableMui
              cols={printManagerCols}
              data={printTableData}
              options={options}
              title=''
            /> */}
          </Grid>
        </Grid>
      </div>
    </>
  )
}

const RenderParams = ({ data, parentKey = null, indentLevel = 0 }) => {
  return (
    <div style={{ marginLeft: `${indentLevel * 20}px` }}>
      {Object.keys(data).map(key => (
        <div key={key}>
          {typeof data[key] !== 'object' ? (
            <Typography variant='body1'>
              {key}: {data[key]}
            </Typography>
          ) : (
            <div>
              {parentKey && <strong>{parentKey}:</strong>} {key}:
              <RenderParams data={data[key]} parentKey={key} indentLevel={indentLevel + 1} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};